<template>
  <div class="block contact-form py-24 sm:py-48">
    <div class="container xxl:px-24">
      <div class="mx-auto md:w-3/4 lg:w-1/2">
        <div v-if="!isComplaintForm">
          <h2>{{ component.gravityForm.title }}</h2>
          <p>{{ component.gravityForm.description }}</p>
        </div>
        <form
          v-if="!formSuccessContent"
          :id="'gform_' + component.gravityForm.id"
          :name="'gform_' + component.gravityForm.id"
          class="form"
        >
          <!-- product search -->
          <div v-if="isComplaintForm" class="relative w-full">
            <div>
              <label>
                <span class="block my-12">
                  {{ $lang('search', 'placeholder') }}
                </span>
              </label>
              <TextInput
                id="productSearch"
                class="search-input"
                :class="{ 'has-active-product cursor-pointer': hasActiveProduct }"
                :placeholder="$lang('search', 'placeholder')"
                :has-error="status === LoadingStatuses.ERROR"
                has-search-icon
                :has-clear-icon="hasActiveProduct"
                :is-loading="status === LoadingStatuses.LOADING"
                :init-value="query"
                @submit-input="(i) => query = i"
                @on-blur="hideResults"
                @on-focus="focusInputClick()"
              />
            </div>
            <div
              v-if="results && results.length && showResults && query"
              class="
              absolute
              h-320
              w-full
              bg-white
              border border-blue-25
              z-30
              shadow
              rounded-lg
              mt-12
            "
            >
              <div class="pt-24 overflow-y-auto h-full md:px-24 px-16 pb-32">
                <div v-if="results.length > 0">
                  <ProductSearchItemSimple
                    v-for="(product, index) in results"
                    :key="product.name + index"
                    :item="product"
                    @set-product-click="setProductClick(product)"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- /product search -->

          <div
            v-for="field in component.gravityForm.fields"
            :key="field.id"
            class="field"
          >
            <div v-if="field.type === 'select' || field.type === 'multiselect'">
              <label>
                <span>{{ field.label }}</span>
              </label>
              <div class="select-container px-16 border relative mb-16">
                <select
                  ref="gFormField"
                  class="select cursor-pointer appearance-none"
                  :name="'input_' + field.id"
                  :required="field.isRequired ? true : false"
                >
                  <option
                    v-if="field.placeholder"
                    selected
                    hidden
                  >
                    {{ field.placeholder }}
                  </option>
                  <option
                    v-for="choice in field.choices"
                    :key="choice.id"
                    :value="choice.value"
                  >
                    {{ choice.text }}
                  </option>
                </select>
                <fa
                  class="
                    absolute
                    top-1/2
                    transform
                    -translate-y-1/2
                    right-16
                    pointer-events-none
                  "
                  :icon="['fal', 'chevron-down']"
                />
              </div>
            </div>
            <div v-else-if="field.type === 'checkbox'" class="my-12">
              <label>
                <span>{{ field.label }}</span>
              </label>
              <label
                v-for="(choice, index) in field.choices"
                :key="choice.id"
                class="flex relative mb-16 cursor-pointer select-none"
              >
                <input
                  ref="gFormField"
                  :name="'input_' + field.id + '_' + (index + 1)"
                  class="absolute opacity-0 cursor-pointer h-0 w-0"
                  type="checkbox"
                  :required="field.isRequired ? true : false"
                  :value="choice.value"
                >
                <span
                  class="
                    checkmark
                    h-24
                    w-24
                    bg-white
                    rounded-md
                    border border-default
                    transition-all
                    duration-200
                    ease-in-out
                    flex
                    items-center
                    justify-center
                    mr-8
                  "
                >
                  <fa
                    class="
                      icon-check
                      opacity-0
                      transition-all
                      duration-200
                      ease-in-out
                      text-sm text-white
                      pt-px
                    "
                    :icon="['fas', 'check']"
                  />
                </span>
                <span class="text-sm font-medium">{{ choice.text }}</span>
              </label>
              <div class="error-text text-warning text-xxs hidden mb-16">
                {{ $lang('forms', 'mandatory_field') }} *
              </div>
            </div>
            <div v-else-if="field.type === 'consent'" class="consent my-12">
              <label
                v-for="(choice, index) in field.choices"
                :key="choice.id"
                class="
                  consent-checkbox
                  container
                  block
                  relative
                  mb-16
                  pl-32
                  cursor-pointer
                  select-none
                "
              >
                <div v-html="field.checkboxLabel" />
                <input
                  ref="gFormField"
                  :name="'input_' + field.id + '_' + (index + 1)"
                  class="absolute opacity-0 cursor-pointer h-0 w-0"
                  type="checkbox"
                  :required="field.isRequired ? true : false"
                  :value="choice.value"
                >
                <span class="checkmark absolute top-0 left-0" />
              </label>
              <div class="error-text text-warning text-xxs hidden mb-16">
                {{ $lang('forms', 'mandatory_field') }} *
              </div>
            </div>
            <div v-else-if="field.type === 'fileupload'" class="mb-16">
              <div class="relative w-fit-content mb-4">
                <label
                  :for="'input_' + component.gravityForm.id + '_' + field.id"
                >
                  <span>{{ field.label }}</span>
                  <button class="btn btn--lg btn--secondary items-center">
                    {{ $lang('forms', 'upload_image') }}
                    <fa class="ml-6" :icon="['fas', 'image']" />
                  </button>
                  <input
                    :id="'input_' + component.gravityForm.id + '_' + field.id"
                    ref="gFormField"
                    class="
                      mb-16
                      absolute
                      w-full
                      top-0
                      left-0
                      h-full
                      opacity-0
                      cursor-pointer
                    "
                    :name="'input_' + field.id"
                    type="file"
                    :required="field.isRequired ? true : false"
                    @input="fileUpload"
                  >
                </label>
              </div>
              <div v-if="uploadedFiles.length > 0">
                {{ $lang('forms', 'uploaded_files') }}:
                <span v-for="file in uploadedFiles" :key="file.id">
                  {{ file.name }}
                </span>
              </div>
            </div>
            <div v-else-if="field.type === 'radio'" class="mb-16">
              <label
                v-for="choice in field.choices"
                :key="choice.id"
                class="flex relative mb-16 cursor-pointer select-none"
              >
                <span>{{ field.label }}</span>
                <input
                  ref="gFormField"
                  :name="'input_' + field.id"
                  class="absolute opacity-0 cursor-pointer h-0 w-0"
                  type="radio"
                  :value="choice.value"
                >
                <span
                  class="
                    checkmark
                    h-24
                    w-24
                    bg-white
                    rounded-md
                    border border-default
                    transition-all
                    duration-200
                    ease-in-out
                    flex
                    items-center
                    justify-center
                    mr-8
                  "
                >
                  <fa
                    class="
                      icon-check
                      opacity-0
                      transition-all
                      duration-200
                      ease-in-out
                      text-sm text-white
                      pt-px
                    "
                    :icon="['fas', 'check']"
                  />
                </span>
                <span class="text-sm font-medium">{{ choice.text }}</span>
              </label>
            </div>
            <div v-else-if="field.type === 'textarea'">
              <label
                :for="'input_' + component.gravityForm.id + '_' + field.id"
              >
                <span>{{ field.label }}</span>
              </label>
              <textarea
                :id="'input_' + component.gravityForm.id + '_' + field.id"
                ref="gFormField"
                class="mb-16"
                :name="'input_' + field.id"
                :type="field.type"
                :placeholder="field.placeholder"
                :maxlength="component.gravityForm.maxLength"
                :required="field.isRequired ? true : false"
              />
            </div>
            <div v-else :class="{ hidden: field.visibility === 'hidden' }">
              <label
                :for="'input_' + component.gravityForm.id + '_' + field.id"
              >
                <span>{{ field.label }}</span>
              </label>
              <input
                :id="'input_' + component.gravityForm.id + '_' + field.id"
                ref="gFormField"
                class="mb-16"
                :class="field.cssClass"
                :name="'input_' + field.id"
                :type="field.type"
                :placeholder="field.placeholder"
                :maxlength="component.gravityForm.maxLength"
                :required="field.isRequired ? true : false"
                :autocomplete="field.autocompleteAttribute"
              >
            </div>

            <div class="error-text text-warning text-xxs hidden mb-16">
              {{ $lang('forms', 'mandatory_field') }} *
            </div>
            <div class="error-text-email text-warning text-xxs hidden mb-16">
              {{ $lang('forms', 'invalid_email') }}
            </div>
          </div>
          <input
            :name="'state_' + component.gravityForm.id"
            :value="component.gravityForm.state"
            type="hidden"
          >
          <button
            class="btn btn--primary btn--lg w-full mt-12"
            @click.prevent="postForm(), scrollToTop()"
          >
            {{ component.gravityForm.button.text }}
          </button>
        </form>
        <div v-if="formSuccessContent" v-html="formSuccessContent" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type ComponentBlock from '~/models/component-block';
import useApiFetch from '~/composables/useApiFetch';
import { useUiStore } from '~/store/ui';
import TextInput from '~/components/forms/TextInput.vue';
import LoadingStatuses from '~/constants/loading-statuses';
import useAlgoliaSearch from '~/composables/useAlgoliaSearch';
import ProductSearchItemSimple from '~/components/search/ProductSearchItemSimple.vue';
import { createSlots } from 'vue';
const { apiPost } = useApiFetch();
const uiStore = useUiStore();
const { algoliaProductSearch } = useAlgoliaSearch();

const props = defineProps<{
  component: ComponentBlock,
  hasSideBar?: boolean,
  isComplaintForm?: boolean,
}>();

const formSuccessContent = ref('');
const error = ref(false);
const uploadedFiles = ref([]);
const gFormField = ref<HTMLInputElement[]>([]);

const fileUpload = (e: any) => {
  uploadedFiles.value = e.target.files;
};

const postForm = async() => {
  formSuccessContent.value = '';
  let errors = false;
  gFormField.value.forEach((field) => {
    // Remove input error classes
    field.classList.remove('field-error');
    field.classList.remove('field-error-email');
    field.parentElement!.classList.remove('field-error');

    if (field.required) {
      if (field.getAttribute('type') === 'email') {
        if (field.value === '') {
          field.classList.add('field-error');
          errors = true;
        } else if (
          !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(field.value.trim())
        ) {
          field.classList.add('field-error-email');
          errors = true;
        }
      } else if (field.getAttribute('type') === 'checkbox') {
        if (!field.checked) {
          field.parentElement!.classList.add('field-error');
          errors = true;
        }
      } else if (field.value === '') {
        field.classList.add('field-error');
        errors = true;
      }
    }
  });
  if (
    errors
  ) {
    return;
  }

  const form = document.getElementById(
    'gform_' + props.component.gravityForm.id
  ) as HTMLFormElement;
  const formData = new FormData(form);
  // Add state to form data
  formData.append(
    props.component.gravityForm.id.toString(),
    'state_' + props.component.gravityForm.state
  );

  const res = await apiPost(
    `/wp-json/api/v1/submit-gravityform/${props.component.gravityForm.id}`,
    formData
  );
  if (res && res.is_valid) {
    formSuccessContent.value = res.confirmation_message;
  } else {
    uiStore.setError('Couldn post form');
  }
};

const scrollToTop = () => {
  window.scrollTo(0,0);
};

// * * * * * * * * * * * * * * * * * * * * * PRODUCT SEARCH
const status = ref('');
const results = ref([]);
const hasActiveProduct = ref(false);
const query = ref('');
const showResults = ref(false);

const focusInputClick = () => {
  if (hasActiveProduct.value) {
    hasActiveProduct.value = false;
    clearInput();
  }

  if (query.value) {
    showResults.value = true;
  }
};

watch(query, ()=> {
  search();
});

const search = async() => {
  if (query.value.trim() !== '') {
    results.value = await algoliaProductSearch(query.value);
    showResults.value = true;
  }
};

const setProductClick = (product: { name: string, sku: string }) => {
  hasActiveProduct.value = true;
  query.value = product.name;
  const inputSearchProduct = document.querySelector(
    '.search_product_data'
  ) as HTMLInputElement;
  if (inputSearchProduct) {
    inputSearchProduct.value = product.name + '(' + product.sku + ')';
  }
};

const hideResults = () => {
  setTimeout(()=> {
    showResults.value = false;
  }, 250);
};
const clearInput = () =>  {
  query.value = '';
};

</script>

<style scoped lang="postcss">

</style>
